/* ═══ AI START: A QUOTE DISCOUNT FROM YOUR NOTES (2026-10-09) ═══
   Ivin: when a job is discounted (a $1,000 flat deal), the quote shows every
   line at its full standard price and ONE quote-level discount brings it down
   to the agreed number. AI Start used to leave that to a second refine step.

   What YOUR NOTES can say, as a line of its own (one discount per brief; a
   leading "-", "*" or "1." and a trailing period are fine):
     Quote discount: 15%                 a percent of the line total
     Quote discount: $500                a fixed dollar amount
     Discount to a total of $1,000       a target total; the tool works out the
                                         dollar discount that lands on it

   Rules this module enforces, in code, so the model never moves money:
   - Lines always price at the catalog / tier rate (applyAIResult already does
     that); the discount is the only thing that lowers the total.
   - Only a directive LINE in the notes applies a discount. The phrase inside a
     sentence ("Do not apply Quote discount: 15%", "client asked: Discount to a
     total of $1,000?") is not a directive. The model reports what it read
     (`quote_discount`, intent and number only); when that is not backed by a
     directive line, nothing is added and the notice asks for the line. So a
     client email, an attachment or a model mistake can never discount a quote.
   - The discount is written in the shape manual and refine discounts already
     use, `{ key, name, type: "%" | "$", value, reason }`, named "Quote
     Discount", so the PDF prints it like any other: "Quote Discount (15%)".
   - A line that starts like a directive but does not parse ("Discount to a
     total of 1000", "Quote discount: $1k") is refused with a notice naming it,
     and so is a brief with more than one discount line.
   - A discount line in the notes (well-formed or not) REPLACES the quote's
     earlier quote discounts (a re-run of AI Start re-prices every line too),
     with the new one or, when it is refused, with none, so the total always
     matches what the notice says. Pro bono discounts are never touched.
   - "Total" means the Quote Builder's "Quote Total" (the PDF's "Total"):
     calcTotals(...).total, the line subtotal minus the quote discounts. The
     Suite charges no sales tax and a deposit is a share OF that total, not a
     deduction from it, so there is no pre-tax / after-tax split: the target
     is the number printed as the total, to the cent.
   - A target at or above the line total (as printed, to the cent) adds no
     discount (never a negative one, never a markup) and says so. Any discount
     that would take the total to $0 is refused: free work goes through Pro bono.
   - Only notes the operator wrote count (quote.notesSource "operator", see
     notesAreOperator below and nextNotesSource in ai.js): typed in the Your
     Notes box, or under a YOUR NOTES style line in One brief. Notes cut at a
     bare "Notes" line (maybe the client's), a website inquiry's text, or notes
     saved before this field existed do not, until the operator edits them.
   - Pro bono stays separate and never stacks: when the quote is already pro
     bono, or the brief says the whole job is free (the model's `free_work`, or
     a notes line opening "Pro bono...", "Free job...", "This project is
     free"), nothing is added and nothing is changed.
   - LiDAR and drone lines are priced later by their estimators, so a TARGET
     total cannot be computed at AI Start while either is in scope (LiDAR
     detected, the model's drone_detected, or a notes line naming drone or
     aerial work that does not exclude it); it is not added, and the notice
     gives the sum to do once the estimator lines are in.
     A percent or dollar discount does not depend on the lines and is added,
     unless it would already take the lines priced so far to $0.

   Pure module: no React, no Firebase. */
import { calcTotals } from "./builderHelpers.js";
import { isProBono, hasProBonoDiscount, isProBonoDiscount } from "./proBono.js";

export const AI_START_DISCOUNT_NAME = "Quote Discount";

// Money to the cent. EPSILON keeps 1.005-style float noise from rounding down.
const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

// A directive is a whole line: optional bullet or number, the phrase, optional
// trailing period. Dollar figures need the "$" and look like "$1,234.50" or
// "$1234.5"; anything else on the line ("$1k", "15% off travel", "?") means it
// is not a directive.
const LEAD = String.raw`^\s*(?:[-*•]\s*|\d+[.)]\s*)?`;
const TAIL = String.raw`\s*\.?\s*$`;
const DOLLARS = String.raw`\$\s*(\d{1,3}(?:,\d{3})+(?:\.\d{1,2})?|\d+(?:\.\d{1,2})?)`;
const DIRECTIVES = [
  { kind: "percent", re: new RegExp(String.raw`${LEAD}quote\s+discount\s*:?\s*(\d+(?:\.\d+)?)\s*%${TAIL}`, "i") },
  { kind: "amount", re: new RegExp(String.raw`${LEAD}quote\s+discount\s*:?\s*${DOLLARS}${TAIL}`, "i") },
  { kind: "target_total", re: new RegExp(String.raw`${LEAD}discount\s+to\s+(?:a\s+)?total(?:\s+of)?\s*:?\s*${DOLLARS}${TAIL}`, "i") },
];
// A line that starts like a directive but does not parse as one.
const NEAR_MISS = new RegExp(String.raw`${LEAD}(?:quote\s+discount\b|discount\s+to\s+(?:a\s+)?total\b)`, "i");

// Free work. Whether the WHOLE job is free is a reading of prose ("Do not charge
// for travel" is not; "Not charged: student visit" is), so the model answers it
// (`free_work`, prompt rule in ai.js). In code, only a notes line that OPENS with
// a whole-job free statement counts on its own ("Pro bono for the NYU class",
// "Free job for NYU", "This project is free"); a mention mid-sentence ("Client
// asked about pro bono", "Parking is free of charge") does not. A wrong "free"
// only withholds the discount with a notice; it never moves money.
const JOB = String.raw`(?:job|project|shoot|session|visit|work|day)`;
// "Pro bono: no, charge standard rates" / "Pro bono? No" answer the question no.
// So do "Pro bono is not needed", "Pro bono discount is not needed", "Free job isn't
// requested", "Pro bono: declined" (up to two words before the denial). "No charge"
// / "no cost" is not a denial: "Pro bono, no charge" is free work.
const DENIAL = String.raw`(?:no\b(?!\s+(?:charge|cost|fee))|not\b|n\/a\b|none\b|false\b|declined\b|(?:is|are|was|were)\s+not\b|isn't\b|aren't\b|wasn't\b|weren't\b)`;
const ANSWERED_NO = String.raw`(?!(?:\s+[\w-]+){0,2}?\s*[:?-]?\s*${DENIAL})`;
const FREE_LINE = new RegExp(String.raw`^\s*(?:[-*•]\s*)?(?:pro[\s-]?bono\b|free\s+(?:of\s+charge\b|${JOB}\b)|(?:this|the)\s+(?:${JOB}\s+)?is\s+(?:free|pro[\s-]?bono)\b)${ANSWERED_NO}`, "i");
export const notesSayFreeWork = (notesText) => String(notesText ?? "").split(/\r?\n/).some((line) => FREE_LINE.test(line));

// Drone work still to be priced: the model's flag, or any notes line that names
// drone or aerial work, unless the WHOLE line only excludes it ("No drone work.",
// "Aerial capture excluded."). Anything else naming drone counts, so a doubtful
// line blocks a target (the safe side) rather than letting one through.
const DRONE_WORD = /\b(?:drones?|aerial)\b/i;
const DRONE_WHAT = String.raw`(?:drones?|aerial)(?:\s+(?:work|capture|flights?|scans?|photogrammetry|pass))?`;
const DRONE_EXCLUDED_LINE = new RegExp(String.raw`^\s*(?:[-*•]\s*)?(?:(?:no|without|skip)\s+${DRONE_WHAT}|${DRONE_WHAT}\s+(?:is\s+|are\s+)?(?:excluded|not\s+needed|not\s+included))\s*\.?\s*$`, "i");
const droneInNotes = (notesText) => String(notesText ?? "").split(/\r?\n/).some((line) => DRONE_WORD.test(line) && !DRONE_EXCLUDED_LINE.test(line));

const toNumber = (v) => {
  if (typeof v === "number") return v;
  if (typeof v !== "string") return NaN;
  const s = v.replace(/[$,\s%]/g, "");
  return s === "" ? NaN : Number(s);
};

const usd = (n) => `$${round2(n).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
const pct = (n) => `${n}%`;

/** The notes read line by line: every discount directive line, in order, and
 *  every line that looks like one but is not. Pure; exported for tests. */
export function parseNotesDiscounts(notesText) {
  const directives = [];
  const nearMisses = [];
  for (const line of String(notesText ?? "").split(/\r?\n/)) {
    let hit = null;
    for (const { kind, re } of DIRECTIVES) {
      const m = re.exec(line);
      if (m) { hit = { kind, value: toNumber(m[1]), phrase: line.trim() }; break; }
    }
    if (hit && Number.isFinite(hit.value)) {
      directives.push(hit); // every line counts: two lines, even identical ones, refuse
    } else if (NEAR_MISS.test(line)) {
      nearMisses.push(line.trim());
    }
  }
  return { directives, nearMisses };
}

/** The model's `quote_discount` reduced to { kind, value } or null. */
export function sanitizeModelDiscount(raw) {
  if (!raw || typeof raw !== "object") return null;
  const kind = ["percent", "amount", "target_total"].includes(raw.type) ? raw.type : null;
  const value = toNumber(raw.value);
  if (!kind || !Number.isFinite(value)) return null;
  return { kind, value };
}

const describeIntent = (intent) => (intent.kind === "percent"
  ? `Quote discount: ${pct(intent.value)}`
  : intent.kind === "amount" ? `Quote discount: ${usd(intent.value)}` : `Discount to a total of ${usd(intent.value)}`);

const SYNTAX_HINT = `To apply one, put it on a line of its own in YOUR NOTES: "Quote discount: 15%", "Quote discount: $500" or "Discount to a total of $1,000".`;

/* The $ value that makes calcTotals land EXACTLY on the target. The rounded
   difference is right in practice; the neighbours by one cent cover float noise
   and lines priced in fractions of a cent. null when none lands. */
function exactTargetDiscount(lineItems, target) {
  const sub = calcTotals(lineItems, []).subtotal;
  const base = round2(sub - target);
  for (const value of [base, round2(base - 0.01), round2(base + 0.01)]) {
    if (value <= 0) continue;
    if (calcTotals(lineItems, [{ type: "$", value }]).total === target) return value;
  }
  return null;
}

/**
 * Decide the quote discount AI Start applies.
 * @param parsed        the extraction reply (reads `quote_discount`, `drone_detected`)
 * @param lineItems     the lines AI Start just priced (catalog / tier rates)
 * @param quote         the quote being extracted into (billing, prior discounts)
 * @param notesText     the YOUR NOTES box
 * @param notesTrusted  notesAreOperator(quote) when AI Start ran; false: no discount
 *                      line counts (the notes may not be the operator's)
 * @param lidarDetected whether LiDAR scope is waiting for the estimator
 * @returns {{ quoteDiscounts?: object[], notice: string }}
 *   quoteDiscounts is present only when it changes; notice is "" when neither
 *   the notes nor the model mention a discount (behaviour then is exactly as
 *   before).
 */
export function resolveAIStartDiscount(parsed, { lineItems = [], quote = {}, notesText = "", notesTrusted = true, lidarDetected = false } = {}) {
  const { directives, nearMisses } = parseNotesDiscounts(notesText);
  const modelRead = sanitizeModelDiscount(parsed?.quote_discount);

  const discountLines = [...directives.map(describeIntent), ...nearMisses.map((l) => `"${l}"`)];

  if (discountLines.length === 0) {
    if (modelRead) {
      return { notice: `The AI read a discount in the brief (${describeIntent(modelRead)}) that is not a discount line in YOUR NOTES, so none was added. ${SYNTAX_HINT}` };
    }
    return { notice: "" };
  }

  // Notes the operator may not have written (notesAreOperator): nothing under them
  // moves money, and nothing changes.
  if (!notesTrusted) {
    return {
      notice: `The discount line (${discountLines.join("; ")}) was not added: a discount counts only in notes you wrote. These notes may not be yours (cut at a bare "Notes" line that can be part of the client's email, a website inquiry, or notes saved before discount lines existed). Type the line in the Your Notes box, or under YOUR NOTES in One brief, and run AI Start again.${(quote?.quoteDiscounts || []).length ? " The quote's earlier discounts were left as they were." : ""}`,
    };
  }

  // Pro bono is its own path (the switch, or refine): never stack a discount on it,
  // and never touch the quote's discounts.
  if (isProBono(quote) || hasProBonoDiscount(quote?.quoteDiscounts)) {
    return {
      notice: `The discount in your notes (${discountLines.join("; ")}) was not added: this quote is pro bono, which already takes the total to $0. Turn Pro bono off in the builder if the job is charged after all.`,
    };
  }
  if (parsed?.free_work === true || notesSayFreeWork(notesText)) {
    return {
      notice: `The discount in your notes (${discountLines.join("; ")}) was not added: your notes say this job is free (pro bono), and a discount never stacks on pro bono. Turn on Pro bono in the builder, or remove the free-work line and run AI Start again.`,
    };
  }

  // From here the notes carry a discount line (well-formed or not), so it replaces
  // the earlier quote discounts: with the new one, or with none when it is refused.
  const priorOthers = (quote?.quoteDiscounts || []).filter((d) => !isProBonoDiscount(d));
  const replaced = priorOthers.length
    ? ` The earlier quote discount${priorOthers.length > 1 ? "s were" : " was"} removed (${priorOthers.map((d) => d.name || "Discount").join(", ")}).`
    : "";
  // The line total as the builder prints it: calcTotals rounds the total to the cent.
  const lineTotal = calcTotals(lineItems, []).total;
  const refuse = (why) => ({ quoteDiscounts: [], notice: `${why}${replaced}` });

  if (nearMisses.length) {
    return refuse(`This line in your notes was not understood as a discount, so no discount was added: "${nearMisses[0]}". ${SYNTAX_HINT}`);
  }
  if (directives.length > 1) {
    return refuse(`Your notes state more than one discount (${discountLines.join("; ")}), so none was added. Keep one and run AI Start again, or add it in the builder.`);
  }
  const intent = directives[0];
  const asked = describeIntent(intent);
  const pending = lidarDetected || parsed?.drone_detected === true || droneInNotes(notesText);
  // Write the discount, unless it would take the total to $0: free work is Pro
  // bono, never a discount. With estimator lines still to come, a discount larger
  // than the lines priced so far waits for them.
  const write = (discount, summary) => {
    const quoteDiscounts = [{ key: `qd_ai_${Date.now()}`, name: AI_START_DISCOUNT_NAME, reason: `From your notes: ${asked}`, ...discount }];
    const total = calcTotals(lineItems, quoteDiscounts).total;
    if (!(total > 0)) {
      return refuse(pending
        ? `"${asked}" was not added yet: the lines priced so far come to ${usd(lineTotal)}, and LiDAR or drone lines are still to come from the estimator. Add it in the builder once they are in.`
        : `"${asked}" was not added: it would take the Quote Total (line total ${usd(lineTotal)}) to $0. For free work, use Pro bono.`);
    }
    // A discount too small to move the total by a cent (0.001%) would print as -$0.
    if (lineTotal > 0 && !(round2(lineTotal - total) >= 0.01)) {
      return refuse(`"${asked}" was not added: on a line total of ${usd(lineTotal)} it comes to less than a cent.`);
    }
    const later = pending ? " LiDAR or drone lines from the estimator are still to come and will change these." : "";
    return { quoteDiscounts, notice: `${summary} Line total ${usd(lineTotal)}, Quote Total ${usd(total)}.${later}${replaced}` };
  };

  if (intent.kind === "percent") {
    const value = Number(intent.value); // kept as written (12.345% stays 12.345%); calcTotals rounds the money
    if (!(value > 0 && value < 100)) {
      return refuse(`"${asked}" was not added: a percent discount must be more than 0% and less than 100%. For free work, use Pro bono.`);
    }
    return write({ type: "%", value }, `Added Quote Discount (${pct(value)}) from your notes.`);
  }

  if (intent.kind === "amount") {
    const value = round2(intent.value);
    if (!(value > 0)) return refuse(`"${asked}" was not added: the amount must be more than $0.`);
    // With estimator lines still to come the line total is not known yet; the
    // totals math caps a discount at the subtotal, so the total never goes negative.
    return write({ type: "$", value }, `Added Quote Discount of ${usd(value)} from your notes.`);
  }

  // target_total
  const target = round2(intent.value);
  if (!(target > 0)) {
    return refuse(`"${asked}" was not added: a target total must be more than $0. For free work, use Pro bono.`);
  }
  if (pending) {
    return refuse(`"${asked}" was not added yet: LiDAR or drone work is priced by its estimator after AI Start, so the target cannot be computed now. Once those lines are in, add a $ quote discount of the line total minus ${usd(target)}.`);
  }
  if (target >= lineTotal) {
    return refuse(`"${asked}" was not added: the lines at standard rates come to ${usd(lineTotal)}, which is ${target === lineTotal ? "already the target" : "below the target"}, so no discount is needed. The Quote Total is ${usd(lineTotal)}; a discount never raises a total.`);
  }
  const value = exactTargetDiscount(lineItems, target);
  if (value == null) {
    // Only reachable when a line is priced in fractions of a cent (a custom rate
    // with more than two decimals, or a fractional quantity) right at a half cent:
    // the shared totals math rounds only the final total, so no whole-cent
    // discount lands on the target.
    return refuse(`"${asked}" was not added: the lines come to a fraction of a cent (${calcTotals(lineItems, []).subtotal}), so no discount in whole cents lands exactly on ${usd(target)}. Round that line's rate to the cent and run AI Start again, or add the discount in the builder for a total one cent off.`);
  }
  return write(
    { type: "$", value },
    `Added Quote Discount of ${usd(value)} so the Quote Total is ${usd(target)} (your target). Lines added later change the total.`,
  );
}

/* Only notes the operator wrote count for a discount line (see nextNotesSource in
   ai.js for how `notesSource` is kept): "operator", or empty notes. Anything else,
   including no field on notes saved before 2026-10-09, is not trusted. */
export const notesAreOperator = (quote) =>
  quote?.notesSource === "operator" || (!quote?.notesSource && !String(quote?.notesText ?? "").trim());

/* AI Start runs for seconds while the notes stay editable. A discount comes only
   from the notes the request read, with the trust they had then, on the quote it
   started for: the builder checks this before applying (and in its state updater),
   and on a mismatch applies the extraction with no discount line (quote discounts
   untouched). A different quote gets nothing at all. Pure; tested. */
export const notesStillMatch = (q, startQuoteId, notesText, notesTrusted = true) =>
  q?.id === startQuoteId
  && String(q?.notesText ?? "").trim() === String(notesText ?? "").trim()
  && notesAreOperator(q) === !!notesTrusted;
export const hasDiscountLine = (notesText) => {
  const { directives, nearMisses } = parseNotesDiscounts(notesText);
  return directives.length + nearMisses.length > 0;
};
export const STALE_NOTES_NOTICE = "Your notes changed while AI Start was running, so the discount line in them was not applied. Run AI Start again to apply it.";
